import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import { AdminSidebar } from '@/components/layout/AdminSidebar'
import { cn } from '@/lib/utils'

interface Episode {
  id: number; title: string; slug: string; duration: string
  difficulty: string; status: string; tags: string[]
  series: { title: string } | null
}

interface Props {
  episodes: Episode[]
  filters: { status?: string; difficulty?: string }
}

const STATUS_COLORS: Record<string, string> = {
  published: 'bg-green-100 text-green-700',
  scheduled: 'bg-blue-100 text-blue-700',
  draft: 'bg-gray-100 text-gray-500',
}

export default function Index({ episodes, filters }: Props) {
  const [search, setSearch] = useState('')

  const filtered = episodes.filter(e =>
    e.title.toLowerCase().includes(search.toLowerCase())
  )

  const setFilter = (key: string, val: string | null) => {
    const params: Record<string, string> = {}
    const current = { ...filters, [key]: val ?? undefined }
    Object.entries(current).forEach(([k, v]) => { if (v) params[k] = v })
    router.get('/admin/episodes', params, { preserveScroll: true })
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar active="/admin/episodes" />

      <main className="flex-1 p-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Episodes</h1>
          <Link href="/admin/episodes/new">
            <Button className="gap-1">+ New episode</Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search episodes..."
              className="pl-8 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/20 w-64"
            />
          </div>
          <select
            value={filters.status ?? ''}
            onChange={e => setFilter('status', e.target.value || null)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="published">Published</option>
          </select>
          <select
            value={filters.difficulty ?? ''}
            onChange={e => setFilter('difficulty', e.target.value || null)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none"
          >
            <option value="">All levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Title</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Duration</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(ep => (
                <tr key={ep.id} className="hover:bg-gray-50/50">
                  <td className="px-5 py-4">
                    <p className="font-medium text-gray-900 text-sm">{ep.title}</p>
                    <p className="text-xs text-gray-400 capitalize mt-0.5">
                      {ep.difficulty} · {ep.tags.slice(0, 3).join(' · ')}
                    </p>
                  </td>
                  <td className="px-5 py-4 text-sm font-mono text-gray-500">{ep.duration}</td>
                  <td className="px-5 py-4">
                    <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full capitalize', STATUS_COLORS[ep.status] ?? 'bg-gray-100 text-gray-500')}>
                      {ep.status}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <Link href={`/admin/episodes/${ep.slug}/edit`} className="text-sm text-gray-600 hover:text-gray-900 mr-3">Edit</Link>
                    <Link
                      href={`/admin/episodes/${ep.slug}`}
                      method="delete"
                      as="button"
                      className="text-sm text-red-500 hover:text-red-700"
                      onClick={(e) => { if (!confirm('Delete this episode?')) e.preventDefault() }}
                    >
                      Delete
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="py-16 text-center text-gray-400 text-sm">No episodes found.</div>
          )}
        </div>
      </main>
    </div>
  )
}
